import { Mic } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { Loading } from '@/components/shared';
import { TokenLimitTooltip } from '@/components/shared/TokenLimitTooltip';
import { ChatInput } from '@/components/ui/chat-input';
import type { DocumentSelection } from '@/types';
import { IDLE_TIMEOUT_MS, useDictation } from '../hooks';
import { PromptSuggestions } from '../PromptSuggestions';
import { shouldSubmitComposerKeyDown } from './composerKeyDown';
import { QuotedExcerpt } from './QuotedExcerpt';
import { SelectedDocumentsList } from './SelectedDocumentsList';

interface PromptSuggestion {
  key: string;
  value: string;
}

interface ChatInputAreaProps {
  /** Ref for the textarea input */
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
  /** Current input value */
  userInput: string;
  /** Callback when input changes */
  onInputChange: (value: string) => void;
  /** Callback to send message */
  onSend: () => Promise<void>;
  /** Callback to abort streaming */
  onAbort: () => void;
  /** Whether currently streaming a response */
  isStreaming: boolean;
  /** Whether the current stream can be safely aborted */
  canAbortStreaming?: boolean;
  /** Whether files are being uploaded */
  isUploading: boolean;

  // Web search props
  webSearchEnabled?: boolean;
  onWebSearchChange?: (enabled: boolean) => void;
  showWebSearchToggle?: boolean;

  // Token limit props
  tokenLimitInfo: {
    message: string;
    isWithinLimit: boolean;
  };

  // Document selection props
  documentSelections: Map<string, DocumentSelection>;
  showAllDocuments: boolean;
  onShowAllDocuments: () => void;
  onAttachmentClick: () => void;
  onOpenDocument: (guid: string, e: React.MouseEvent) => void;
  onRemoveDocument: (guid: string) => void;
  onToggleParent: (guid: string) => void;
  onToggleAttachment: (parentGuid: string, attachGuid: string) => void;
  onClearAllDocuments?: () => void;

  // Prompt library props
  onPromptLibraryClick: () => void;

  matterGuid?: string;
  chatGuid?: string | null;

  // Prompt suggestions props (optional, used in welcome view)
  promptSuggestions?: PromptSuggestion[];
  showPromptSuggestions?: boolean;
  onSuggestionClick?: (value: string) => void;
  onCloseSuggestions?: () => void;

  // Focus state
  onFocus?: () => void;
  onBlur?: () => void;

  // Matter memory props (for overview updating indicator)
  isMatterMemoryGenerating?: boolean;

  /** Variant determines visual differences between welcome and conversation views */
  variant: 'welcome' | 'conversation';
  quotedExcerpt?: string | null;
  onClearQuote?: () => void;
}

/**
 * Unified chat input area component used in both welcome screen and conversation view
 * Eliminates code duplication by parameterizing differences via props
 */
export const ChatInputArea: React.FC<ChatInputAreaProps> = ({
  inputRef,
  userInput,
  onInputChange,
  onSend,
  onAbort,
  isStreaming,
  canAbortStreaming = true,
  isUploading,
  webSearchEnabled = false,
  onWebSearchChange,
  showWebSearchToggle = false,
  tokenLimitInfo,
  documentSelections,
  showAllDocuments,
  onShowAllDocuments,
  onAttachmentClick,
  onOpenDocument,
  onRemoveDocument,
  onToggleParent,
  onToggleAttachment,
  onClearAllDocuments,
  onPromptLibraryClick,
  matterGuid,
  chatGuid,
  promptSuggestions = [],
  showPromptSuggestions = false,
  onSuggestionClick,
  onCloseSuggestions,
  onFocus,
  onBlur,
  isMatterMemoryGenerating = false,
  variant,
  quotedExcerpt,
  onClearQuote,
}) => {
  // Max visible documents differs by variant
  const maxVisibleDocs = variant === 'welcome' ? 2 : 3;

  // Voice dictation: the browser streams mic audio directly to Azure AI Speech and
  // writes the transcript straight into this composer (fill-and-review; never
  // auto-sends). Owned here so every ChatInputArea (full page + side panel) gets the
  // mic just by supplying the conversation context — no per-call prop wiring.
  const dictation = useDictation({
    matterGuid,
    chatGuid,
    value: userInput,
    onChange: onInputChange,
    // Let the user know why the mic switched off on its own after they went quiet.
    // Derive the duration from the hook's constant so the copy can't drift from it.
    onIdleStop: () =>
      toast(`Dictation stopped after ${Math.round(IDLE_TIMEOUT_MS / 1000)} seconds of silence.`, {
        icon: <Mic className="h-4 w-4 text-slate-500" />,
        style: { whiteSpace: 'nowrap', maxWidth: 'none' },
      }),
  });
  const { error: dictationError, clearError: clearDictationError, stop: stopDictation } = dictation;
  const sendInFlightRef = useRef(false);

  useEffect(() => {
    if (!dictationError) return;
    toast.error(dictationError);
    clearDictationError();
  }, [dictationError, clearDictationError]);

  // Sending ends any live dictation so the mic doesn't keep running after submit.
  // Discard any trailing final so it can't repopulate the just-cleared input.
  const handleSend = useCallback(async () => {
    if (sendInFlightRef.current) return;
    sendInFlightRef.current = true;
    try {
      stopDictation({ discard: true });
      await onSend();
    } finally {
      sendInFlightRef.current = false;
    }
  }, [stopDictation, onSend]);

  // Handle key down events
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape' && onCloseSuggestions) {
      onCloseSuggestions();
      return;
    }

    if (!shouldSubmitComposerKeyDown(e)) {
      return;
    }

    e.preventDefault();
    void handleSend();
  };

  // Handle input change with suggestion closing
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onInputChange(e.target.value);
    // Close suggestions when user starts typing
    if (showPromptSuggestions && e.target.value.length > 0 && onCloseSuggestions) {
      onCloseSuggestions();
    }
  };

  // Build selected documents content for ChatInput
  const selectedDocumentsContent =
    isMatterMemoryGenerating || documentSelections.size > 0 ? (
      <div className="flex flex-col gap-2">
        {/* Case overview updating indicator */}
        {isMatterMemoryGenerating && (
          <div className="flex items-center gap-1.5 self-start text-xs text-slate-600">
            <Loading text='Case overview is updating' size='sm' inline />
          </div>
        )}

        {/* Selected documents */}
        {documentSelections.size > 0 && (
          <SelectedDocumentsList
            documentSelections={documentSelections}
            showAll={showAllDocuments}
            onShowAll={onShowAllDocuments}
            maxVisible={maxVisibleDocs}
            onOpenDocument={onOpenDocument}
            onRemoveGroup={onRemoveDocument}
            onToggleParent={onToggleParent}
            onToggleAttachment={onToggleAttachment}
            onClearAll={onClearAllDocuments}
          />
        )}
      </div>
    ) : undefined;

  return (
    <div className="relative">
      {/* Prompt suggestions (only shown in welcome variant when enabled) */}
      {variant === 'welcome' && onSuggestionClick && onCloseSuggestions && (
        <PromptSuggestions
          suggestions={promptSuggestions}
          position="below"
          isVisible={showPromptSuggestions && promptSuggestions.length > 0 && userInput.length === 0}
          onSuggestionClick={(suggestionValue) => {
            onSuggestionClick(suggestionValue);
            inputRef.current?.focus();
          }}
          onClose={onCloseSuggestions}
          onOpenLibrary={() => {
            onPromptLibraryClick();
            onCloseSuggestions();
          }}
        />
      )}

      <TokenLimitTooltip message={tokenLimitInfo.message} disabled={!tokenLimitInfo.isWithinLimit}>
        <ChatInput
          ref={inputRef}
          autoFocus
          value={userInput}
          onChange={handleChange}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={handleKeyDown}
          isSearching={isStreaming}
          actionDisabled={isStreaming && !canAbortStreaming}
          isUploading={isUploading}
          onClickButton={async () => (isStreaming ? onAbort() : await handleSend())}
          onAttachmentClick={onAttachmentClick}
          onPromptLibraryClick={onPromptLibraryClick}
          onDictateClick={dictation.toggle}
          onDictatePrewarm={dictation.prewarm}
          isDictating={dictation.isRecording}
          isDictationConnecting={dictation.isConnecting}
          dictationSupported={dictation.isSupported}
          attachmentDisabled={!tokenLimitInfo.isWithinLimit}
          maxLength={100000}
          disabled={!tokenLimitInfo.isWithinLimit}
          webSearchEnabled={webSearchEnabled}
          onWebSearchChange={onWebSearchChange}
          showWebSearchToggle={showWebSearchToggle}
          selectedDocuments={selectedDocumentsContent}
          quotedExcerpt={quotedExcerpt ? (
            <QuotedExcerpt text={quotedExcerpt} onClear={onClearQuote} />
          ) : undefined}
        />
      </TokenLimitTooltip>
    </div>
  );
};

export default ChatInputArea;
